import apiService from './api';
import type { ApiResponse } from '../types';
import type { ApiError } from '../types/api';
import type { NumericValue } from '@/utils/numeric';
import type { LeaveOnDay } from './leave';

export interface TimesheetEntry {
  id: number;
  user_id: number;
  clock_in_time: string;
  clock_out_time?: string;
  date: string;
  total_hours?: NumericValue;
  status: 'clocked_in' | 'clocked_out';
  notes?: string;
  payroll_category?: string;
  created_at: string;
  updated_at: string;
  // Edit tracking fields
  original_clock_in_time?: string;
  original_clock_out_time?: string;
  edited?: boolean;
  edited_at?: string;
  edit_reason?: string;
  skip_lunch_deduction?: boolean;
  /** Hours claimed off the work schedule because no clock in/out was recorded. */
  missed_clock?: boolean;
  missed_clock_reason?: string | null;
  // Location fields
  clock_in_latitude?: NumericValue;
  clock_in_longitude?: NumericValue;
  clock_in_accuracy?: number;
  clock_out_latitude?: NumericValue;
  clock_out_longitude?: NumericValue;
  clock_out_accuracy?: number;
}

export interface TimesheetStatus {
  status: 'clocked_in' | 'clocked_out' | 'not_clocked_in';
  hasActiveSessions: boolean;
  activeSessionsCount: number;
  activeSessions: Array<TimesheetEntry & { current_hours?: number }>;
  completedSessions: TimesheetEntry[];
  totalHoursToday: number;
  date: string;
  /** Set when you are a guest in the organisation you are working in: your time goes here. */
  paid_by?: { organisation_id: number; name: string } | null;
}

export interface TimesheetSummary {
  total_days_worked: number;
  total_sessions: number;
  completed_sessions: number;
  active_sessions: number;
  total_hours: NumericValue;
  average_hours_per_session: number;
  scheduled_hours: number;
  pay_frequency: string;
  overtime_hours: number;
  overtime_threshold: number;
  /**
   * 'roster' when the threshold is the employee's rostered hours for the
   * period (fortnightly staff with a roster); 'policy' when it is the
   * organisation's fixed figure. Absent from an older server.
   */
  overtime_threshold_basis?: 'roster' | 'policy';
  period_days: number;
  period_start: string;
  period_end: string;
  public_holiday_hours: number;
  public_holiday_worked_hours: number;
  public_holiday_days: number;
  public_holidays: Array<{ date: string; name: string }>;
  has_unsubmitted_entries?: boolean;
}

export interface DailyCategoryRow {
  date: string;
  category: string;
  hours: NumericValue;
}

export interface TimesheetSummaryResponse {
  summary: TimesheetSummary;
  dailyBreakdown: Array<{
    date: string;
    sessions_count: number;
    completed_sessions: number;
    total_hours: NumericValue;
  }>;
  dailyCategoryBreakdown: DailyCategoryRow[];
}

export interface TimesheetHistory {
  entries: TimesheetEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ClockInRequest {
  notes?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

/**
 * A successful clock-in, plus any approved leave already booked on part of the
 * same day.
 *
 * Full-day leave refuses the clock-in outright (409 `LEAVE_ON_THIS_DAY`), but a
 * booked afternoon is ordinary and does not block anything — it is reported
 * here so the finish time is not a surprise at knock-off.
 *
 * An intersection rather than `extends`, because `ApiResponse` is a
 * success/failure union and an interface cannot extend one.
 */
export type ClockInResponse = ApiResponse<TimesheetEntry> & { leave_notice?: LeaveOnDay };

export interface ClockOutRequest {
  notes?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  skip_lunch_deduction?: boolean;
}

export interface UpdateTimesheetRequest {
  clock_in_time: string;
  clock_out_time?: string;
  notes?: string;
  edit_reason: string;
  skip_lunch_deduction?: boolean;
}

/**
 * Clocking on and off gets its own, much longer patience than the rest of the
 * app.
 *
 * The shared axios client times out at 10 s and never retries, which is right
 * for a page that can show a spinner and be reloaded. It is wrong for these
 * two: they are pressed once, in a paddock, on whatever signal is going, and a
 * failure is not a blank panel — it is an employee who is not on the clock.
 * Their fallback is a missed-clock declaration, which pays ROSTERED hours and
 * needs a supervisor, so a needless failure here costs somebody real money and
 * somebody else an approval.
 *
 * 15 s an attempt inside a 45 s budget. Long enough to ride out the reconnect
 * after a truck passes a hill; short enough that a genuinely dead link still
 * gives an answer while the operator is standing there.
 */
const CLOCK_ATTEMPT_TIMEOUT_MS = 15_000;
const CLOCK_RETRY_BUDGET_MS = 45_000;
const CLOCK_BACKOFF_MS = [2_000, 4_000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface ClockEventOptions {
  /**
   * Called when an attempt failed and another is coming, with the retry number.
   * Lets the caller say so on the button instead of leaving the operator
   * watching an unexplained spinner.
   */
  onRetry?: (attempt: number) => void;
}

/**
 * Post a clock event, retrying only while the request never reached the server.
 *
 * Retrying a request that DID arrive is the danger, and a timeout cannot tell
 * you which happened. That is what `alreadyDone` is for: if a later attempt is
 * refused because the thing is already done, an earlier attempt landed and the
 * lost reply was the only casualty — so report success, not an error the
 * employee cannot act on.
 *
 * It is consulted ONLY after a retry. On the first attempt the identical
 * refusal is genuine: an employee really can already be clocked in from
 * another device, and silently swallowing that would hide a real conflict.
 */
async function postClockEvent<T>(
  url: string,
  data: unknown,
  alreadyDone: (err: ApiError) => boolean,
  onRetry?: (attempt: number) => void,
): Promise<ApiResponse<T>> {
  const deadline = Date.now() + CLOCK_RETRY_BUDGET_MS;
  let attempt = 0;

  for (;;) {
    try {
      return await apiService.post<T>(url, data, undefined, {
        timeout: CLOCK_ATTEMPT_TIMEOUT_MS,
      });
    } catch (e) {
      const err = e as ApiError;

      if (attempt > 0 && alreadyDone(err)) {
        // An earlier attempt got through. The caller refetches status straight
        // after this returns, so the absent `data` is picked up there rather
        // than guessed at here.
        return { success: true, message: err.message } as ApiResponse<T>;
      }

      const backoff = CLOCK_BACKOFF_MS[attempt];
      // Only a request with no answer is worth repeating, and only while the
      // budget and the backoff schedule both still have room.
      if (!err.offline || backoff === undefined || Date.now() + backoff >= deadline) {
        throw err;
      }

      // Announce the wait BEFORE sleeping through it. A button that sits on
      // "Processing…" for 45 s reads as a hung app, and the employee's next
      // move is to press it again or walk off believing they are on the clock.
      attempt += 1;
      onRetry?.(attempt);
      await sleep(backoff);
    }
  }
}

class TimesheetService {
  // Clock in
  async clockIn(data: ClockInRequest, opts?: ClockEventOptions): Promise<ClockInResponse> {
    return postClockEvent<TimesheetEntry>(
      '/timesheet/clock-in',
      data,
      // 409 from the open-session guard: a session is already running. After a
      // retry that is our own earlier attempt.
      (err) => err.status === 409 && /already|duplicate/i.test(err.message),
      opts?.onRetry,
    );
  }

  // Clock out
  async clockOut(data: ClockOutRequest, opts?: ClockEventOptions): Promise<ApiResponse<TimesheetEntry>> {
    return postClockEvent<TimesheetEntry>(
      '/timesheet/clock-out',
      data,
      // 400 'No active clock-in found' — after a retry, because our earlier
      // attempt already closed the session.
      (err) => err.status === 400 && /no active clock-in/i.test(err.message),
      opts?.onRetry,
    );
  }

  // Get current status
  async getStatus(): Promise<ApiResponse<TimesheetStatus>> {
    return apiService.get('/timesheet/status');
  }

  // Get timesheet history
  async getHistory(params: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<ApiResponse<TimesheetHistory>> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    const url = queryString ? `/timesheet/history?${queryString}` : '/timesheet/history';
    
    return apiService.get(url);
  }

  // Get timesheet summary
  async getSummary(params: {
    startDate?: string;
    endDate?: string;
    period?: 'today' | 'week' | 'fortnight' | 'month';
  } = {}): Promise<ApiResponse<TimesheetSummaryResponse>> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.period) queryParams.append('period', params.period);

    const queryString = queryParams.toString();
    const url = queryString ? `/timesheet/summary?${queryString}` : '/timesheet/summary';
    
    return apiService.get(url);
  }

  // HR: one employee's hours over a date range, by payroll type and by day.
  async getHoursReport(userId: number, start: string, end: string): Promise<ApiResponse<HoursReport>> {
    const q = new URLSearchParams({ start, end });
    return apiService.get(`/timesheet/admin/hours-report/${userId}?${q.toString()}`);
  }

  // Update timesheet entry
  async updateTimesheet(id: number, data: UpdateTimesheetRequest): Promise<ApiResponse<TimesheetEntry>> {
    return apiService.put(`/timesheet/${id}`, data);
  }

  // Get the user's paused / pending farm jobs and spray applications.
  // Used immediately after a successful clock-in to decide whether to land
  // the user on the "Resume your work" screen.
  async getPostClockinWork(): Promise<ApiResponse<PostClockinWorkPayload>> {
    return apiService.get('/timesheet/post-clockin-work');
  }
}

// ----------------------------------------------------------------------------
// Resume-work types
// ----------------------------------------------------------------------------

/** `worked` is time at work; `leave` draws on an entitlement; `holiday` is a paid public holiday. */
export type HoursKind = 'worked' | 'leave' | 'holiday';

export interface HoursReportCategory {
  category: string;
  kind: HoursKind;
  hours: number;
  entries: number;
}

export interface HoursReportDay {
  date: string;
  /** 1 = Monday … 7 = Sunday */
  weekday: number;
  scheduled_hours: number;
  scheduled_start_time: string | null;
  scheduled_end_time: string | null;
  /** Earliest clock-in / latest clock-out of WORKED entries; leave sets neither. */
  first_in: string | null;
  last_out: string | null;
  total_hours: number;
  worked_hours: number;
  leave_hours: number;
  holiday_hours: number;
  /** Paid hours minus rostered hours. */
  variance_hours: number;
  by_category: Record<string, number>;
  /** Hours on an approved submission — what approval accrued leave on. Absent from an older server. */
  approved_hours?: number;
  /** The 'Time in Lieu' part of `approved_hours`, credited to the balance on approval. */
  approved_til_hours?: number;
  /**
   * The part of `approved_hours` approved before the server kept a record of
   * what each approval added. Only these are estimated. Absent from a server
   * that keeps no record, where every approved hour is.
   */
  unrecorded_approved_hours?: number;
  unrecorded_approved_til_hours?: number;
  entry_count: number;
  /** Clock-ins still open — not counted in any hours figure. */
  open_entries: number;
  missed_clock_entries: number;
  edited_entries: number;
  holiday_name: string | null;
}

export interface HoursReport {
  employee: { id: number; username: string; email: string; first_name: string | null; last_name: string | null };
  start_date: string;
  end_date: string;
  totals: {
    total_hours: number;
    worked_hours: number;
    leave_hours: number;
    holiday_hours: number;
    scheduled_hours: number;
    variance_hours: number;
    days_worked: number;
    days_in_range: number;
    average_worked_per_day_worked: number;
    open_entries: number;
    missed_clock_entries: number;
    edited_entries: number;
  };
  categories: HoursReportCategory[];
  days: HoursReportDay[];
  /**
   * What approvals overlapping the range added to this employee's balances,
   * as recorded when each was approved. Absent from a server that keeps no record.
   */
  accruals?: HoursReportRecordedAccrual[];
  /**
   * What an approved hour adds to this employee's balances, at today's policy
   * rates: the estimate for hours approved before a record was kept. Null or
   * absent when the server could not say (or predates it).
   */
  accrual?: HoursReportAccrual | null;
}

/** One balance one approval added to. `period_*` are the approval's own dates. */
export interface HoursReportRecordedAccrual {
  approval_id: number | null;
  leave_type: string;
  label: string;
  hours: number;
  period_start: string;
  period_end: string;
}

export interface HoursReportAccrual {
  /** 'Time in Lieu' hours are credited to the time-in-lieu balance on approval. */
  time_in_lieu: boolean;
  /** Leave types accrued here; one Xero owns for this employee is left out. */
  leave: { leave_type: string; label: string; hours_per_hour: number }[];
}

export interface PausedJob {
  id: number;
  name: string;
  farm_id: number | null;
  farm_name: string | null;
  target_name: string | null;
  scheduled_start: string | null;
  status: 'paused' | 'assigned';
  paused_at: string | null;
  auto_paused_by_clockout: boolean;
  started_at: string | null;
}

export interface PausedSpray {
  id: number;
  title: string;
  reference_number: string | null;
  scheduled_date: string | null;
  scheduled_start_time: string | null;
  status: 'paused' | 'assigned';
  paused_at: string | null;
  auto_paused_by_clockout: boolean;
  started_at: string | null;
}

export interface PostClockinWorkPayload {
  auto_paused_jobs: PausedJob[];
  manual_paused_jobs: PausedJob[];
  pending_jobs: PausedJob[];
  auto_paused_sprays: PausedSpray[];
  manual_paused_sprays: PausedSpray[];
  pending_sprays: PausedSpray[];
}

export const timesheetService = new TimesheetService();
export default timesheetService;
